import { Icon } from '../../lib/icons';
import { useActivityStore, selectActiveCount, selectHasError } from '../../lib/activityStore';
import { readerLocale } from '../../lib/locale';

/**
 * The single entry point to the Activity Center, mounted in the AppTopbar's
 * right slot. Three states, driven entirely by the two store selectors:
 *  · idle    — subtle glyph, no badge
 *  · running — themed `satellite` (radar) glyph + pulse/sweep animation + count
 *              badge tinted with --accent-primary
 *  · alert   — --accent-error tint + a `warning` (AlertTriangle) glyph; stays red
 *              until every failed activity is dismissed
 *
 * Running animations live in `.activity-indicator-*` CSS classes (see
 * integrationNeeds) and are disabled under prefers-reduced-motion. Click toggles
 * the tray.
 */
export function ActivityIndicator() {
  const activeCount = useActivityStore(selectActiveCount);
  const hasError = useActivityStore(selectHasError);
  const togglePanel = useActivityStore((s) => s.togglePanel);

  const running = activeCount > 0;
  const state: 'idle' | 'running' | 'alert' = hasError ? 'alert' : running ? 'running' : 'idle';

  const tint =
    state === 'alert' ? 'var(--accent-error)' : state === 'running' ? 'var(--accent-primary)' : 'var(--text-tertiary)';

  const label =
    state === 'alert'
      ? 'Activity — attention needed'
      : state === 'running'
        ? `Activity — ${activeCount} running`
        : 'Activity';

  return (
    <button
      type="button"
      className="activity-indicator btn-icon"
      aria-label={label}
      title={label}
      onClick={() => togglePanel()}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 36,
        height: 36,
        background: 'transparent',
        border: 'none',
        borderRadius: 'var(--radius-md)',
        cursor: 'pointer',
        color: tint,
      }}
    >
      {/* Animated sweep ring behind the glyph while running (reduced-motion off). */}
      {state === 'running' && <span className="activity-indicator-sweep" aria-hidden />}

      <span className={state === 'running' ? 'activity-indicator-glyph' : undefined} style={{ display: 'inline-flex' }}>
        <Icon name={state === 'alert' ? 'warning' : 'satellite'} size={18} />
      </span>

      {(running || state === 'alert') && (
        <span
          className="activity-indicator-badge"
          style={{
            position: 'absolute',
            top: 2,
            right: 2,
            minWidth: 16,
            height: 16,
            padding: '0 4px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 10,
            lineHeight: 1,
            fontWeight: 700,
            borderRadius: 999,
            color: 'var(--text-inverse)',
            background: state === 'alert' ? 'var(--accent-error)' : 'var(--accent-primary)',
            boxShadow: '0 0 0 2px var(--bg-elevated)',
          }}
        >
          {state === 'alert' && activeCount === 0 ? '!' : activeCount.toLocaleString(readerLocale())}
        </span>
      )}
    </button>
  );
}
